"use client";

import Link from "next/link";
import { motion, useReducedMotion } from "framer-motion";
import type { LucideIcon } from "lucide-react";
import {
  Cog,
  Crown,
  Lightbulb,
  LineChart,
  ListChecks,
  Rocket,
  Star,
  TrendingUp,
  Users,
} from "lucide-react";
import { t } from "@/content/i18n/messages";
import { defaultLocale, getLocalizedPathname, type Locale } from "@/lib/i18n";

interface CorporateMissionProps {
  locale?: Locale;
}

export default function CorporateMission({
  locale = defaultLocale,
}: CorporateMissionProps) {
  const prefersReducedMotion = useReducedMotion();
  const translate = (text: string) => t(locale, text);

  // Hero entrance moves only: opacity stays 1 so the hero paints before
  // hydration (LCP).
  const reveal = (delay: number) => ({
    initial: prefersReducedMotion ? false : { y: 12 },
    animate: { y: 0 },
    transition: { duration: 0.7, delay, ease: [0.21, 0.6, 0.35, 1] as const },
  });

  const commitments: Array<{
    title: string;
    description: string;
    icon: LucideIcon;
  }> = [
    {
      title: "Customer value",
      description:
        "To create exceptional value for our customers by delivering high-quality services that consistently exceed expectations.",
      icon: Star,
    },
    {
      title: "Empowered people",
      description:
        "To cultivate a workforce that is empowered, skilled, and dedicated, guided by a strong organizational culture.",
      icon: Users,
    },
    {
      title: "Sustainable growth",
      description:
        "To ensure profitability and sustainability, fostering long-term success for the company and its community.",
      icon: LineChart,
    },
  ];

  const visionPillars: Array<{
    title: string;
    description: string;
    icon: LucideIcon;
  }> = [
    {
      title: "Driving Business Growth",
      description:
        "We aim to create technology solutions that accelerate business growth, open new revenue streams, and unlock opportunities for our clients in their respective markets.",
      icon: Rocket,
    },
    {
      title: "Enhancing Efficiency",
      description:
        "We strive to develop solutions that streamline operations, automate repetitive tasks, and optimize processes, allowing our clients to focus on their core business and strategic initiatives.",
      icon: TrendingUp,
    },
    {
      title: "Creating Competitive Advantage",
      description:
        "We work to equip our clients with innovative technology solutions that differentiate them in the marketplace and provide sustainable competitive advantages in their industries.",
      icon: Crown,
    },
  ];

  const approaches: Array<{
    title: string;
    description: string;
    icon: LucideIcon;
  }> = [
    {
      title: "Client Partnership",
      description:
        "We build deep, collaborative partnerships with our clients, taking the time to understand their unique business challenges, objectives, and industry context to deliver solutions that create real impact.",
      icon: Users,
    },
    {
      title: "Innovation Focus",
      description:
        "We constantly explore emerging technologies and innovative approaches, incorporating them into our solutions to address complex business challenges and drive transformative results.",
      icon: Lightbulb,
    },
    {
      title: "Technical Excellence",
      description:
        "We maintain a relentless commitment to technical excellence, building robust, scalable, and secure solutions that perform reliably in demanding business environments.",
      icon: Cog,
    },
    {
      title: "Measurable Results",
      description:
        "We focus on delivering measurable business results, implementing solutions with clear KPIs and performance metrics to demonstrate tangible ROI and business impact.",
      icon: ListChecks,
    },
  ];

  return (
    <div className="bg-background">
      {/* Unified page header */}
      <section className="bg-background pb-16 pt-24 md:pb-20 md:pt-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.p
            {...reveal(0)}
            className="mb-4 text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted"
          >
            {translate("Corporate Mission")}
          </motion.p>
          <motion.h1
            {...reveal(0.08)}
            className="max-w-4xl font-display text-4xl font-bold leading-[1.12] tracking-tight text-neutral-emphasis md:text-5xl lg:text-6xl"
          >
            {translate("Technology that moves")}{" "}
            <span className="emphasis-underline">
              {translate("business forward")}
            </span>
          </motion.h1>
          <motion.p
            {...reveal(0.16)}
            className="mt-5 max-w-2xl text-lg text-neutral-muted"
          >
            {translate(
              "At Anchor Sprint, our mission is to empower businesses through innovative technology solutions that drive growth, enhance efficiency, and create sustainable competitive advantages in an increasingly digital world.",
            )}
          </motion.p>
        </div>
      </section>
      <div className="h-px w-full bg-border" aria-hidden />

      {/* Mission commitments — numbered cards */}
      <section className="bg-background py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 max-w-3xl">
            <p className="mb-4 text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted">
              {translate("The mission")}
            </p>
            <h2 className="font-display text-3xl font-bold leading-tight text-neutral-emphasis md:text-4xl">
              {translate("Our Mission")}
            </h2>
          </div>

          <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
            {commitments.map(({ title, description, icon: Icon }, index) => (
              <motion.div
                key={description}
                initial={prefersReducedMotion ? false : { y: 12 }}
                whileInView={{ y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{
                  duration: 0.6,
                  delay: index * 0.1,
                  ease: [0.21, 0.6, 0.35, 1],
                }}
                className="flex h-full flex-col rounded-2xl border border-border bg-card p-7 transition-colors duration-200 hover:border-neutral-border"
              >
                <div className="mb-6 flex items-start justify-between">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10">
                    <Icon className="h-5 w-5 text-primary" aria-hidden />
                  </div>
                  <span className="font-mono text-sm text-neutral-muted/60">
                    0{index + 1}
                  </span>
                </div>
                <h3 className="mb-3 font-display text-lg font-semibold text-neutral-emphasis">
                  {translate(title)}
                </h3>
                <p className="text-sm leading-relaxed text-neutral-muted">
                  {translate(description)}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Vision */}
      <section className="bg-neutral-surface py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 max-w-3xl">
            <p className="mb-4 text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted">
              {translate("The vision")}
            </p>
            <h2 className="mb-5 font-display text-3xl font-bold text-neutral-emphasis md:text-4xl">
              {translate("Our Vision")}
            </h2>
            <p className="max-w-3xl text-base text-neutral-muted md:text-lg">
              {translate(
                "We envision a world where technology serves as a powerful enabler for business success, where innovation and digital transformation are accessible to all organizations, and where technology solutions are designed with both business objectives and human needs in mind.",
              )}
            </p>
          </div>

          <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
            {visionPillars.map(({ title, description, icon: Icon }, index) => (
              <motion.div
                key={title}
                initial={prefersReducedMotion ? false : { opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{
                  duration: 0.6,
                  delay: index * 0.1,
                  ease: [0.21, 0.6, 0.35, 1],
                }}
                className="flex h-full flex-col rounded-2xl border border-border bg-card p-7 transition-colors duration-200 hover:border-neutral-border"
              >
                <div className="mb-5 flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10">
                  <Icon className="h-5 w-5 text-primary" aria-hidden />
                </div>
                <h3 className="mb-3 font-display text-lg font-semibold text-neutral-emphasis">
                  {translate(title)}
                </h3>
                <p className="text-sm leading-relaxed text-neutral-muted">
                  {translate(description)}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Elevation anchor — the mission as a dark proof moment */}
      <section className="bg-background py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={prefersReducedMotion ? false : { opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, ease: [0.21, 0.6, 0.35, 1] }}
            className="rounded-3xl bg-hero-background p-10 sm:p-14"
          >
            <p className="mb-4 text-sm font-semibold uppercase tracking-[0.3em] text-primary-contrast/60">
              {translate("The mission in practice")}
            </p>
            <h2 className="max-w-3xl font-display text-3xl font-bold leading-tight text-primary-contrast md:text-4xl">
              {translate(
                "Empowering businesses is a delivery record, not a slogan.",
              )}
            </h2>
            <div className="mt-10 grid grid-cols-1 gap-8 border-t border-primary-contrast/15 pt-8 sm:grid-cols-3">
              <div>
                <div className="font-display text-4xl font-bold text-primary-contrast md:text-5xl">
                  16+
                </div>
                <p className="mt-2 text-sm text-primary-contrast/70">
                  {translate("Years of technology leadership")}
                </p>
              </div>
              <div>
                <div className="font-display text-4xl font-bold text-primary-contrast md:text-5xl">
                  25+
                </div>
                <p className="mt-2 text-sm text-primary-contrast/70">
                  {translate("Projects delivered")}
                </p>
              </div>
              <div>
                <div className="font-display text-4xl font-bold text-primary-contrast md:text-5xl">
                  {translate("MDEC")}
                </div>
                <p className="mt-2 text-sm text-primary-contrast/70">
                  {translate("Malaysia Digital certified")}
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Strategic approach */}
      <section className="bg-background py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-12 max-w-3xl">
            <p className="mb-4 text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted">
              {translate("The approach")}
            </p>
            <h2 className="font-display text-3xl font-bold text-neutral-emphasis md:text-4xl">
              {translate("Our Strategic Approach")}
            </h2>
          </div>

          <div className="mx-auto grid max-w-5xl grid-cols-1 gap-6 md:grid-cols-2">
            {approaches.map(({ title, description, icon: Icon }, index) => (
              <motion.div
                key={title}
                initial={prefersReducedMotion ? false : { opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{
                  duration: 0.6,
                  delay: index * 0.08,
                  ease: [0.21, 0.6, 0.35, 1],
                }}
                className="flex h-full items-start gap-5 rounded-2xl border border-border bg-card p-6 transition-colors duration-200 hover:border-neutral-border"
              >
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10">
                  <Icon className="h-5 w-5 text-primary" aria-hidden />
                </div>
                <div>
                  <h3 className="mb-2 font-display text-base font-semibold text-neutral-emphasis">
                    {translate(title)}
                  </h3>
                  <p className="text-sm leading-relaxed text-neutral-muted">
                    {translate(description)}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA band */}
      <section className="bg-neutral-surface py-24 lg:py-32">
        <div className="mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-neutral-emphasis md:text-5xl">
            {translate("Join us on")}{" "}
            <span className="emphasis-underline">
              {translate("our mission")}
            </span>
          </h2>
          <p className="mt-5 text-lg text-neutral-muted">
            {translate(
              "Partner with us to empower your business through innovative technology solutions that drive growth, enhance efficiency, and create sustainable competitive advantages.",
            )}
          </p>
          <div className="mt-9">
            <Link
              href={getLocalizedPathname("/contact/", locale)}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-primary px-8 py-4 font-semibold text-primary-foreground transition hover:bg-primary-emphasis"
            >
              {translate("Meet with Us")}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
